import { AssetLoaderModule, CustomDatePipe } from '@abovo/shared-angular-utils';
import { ThemeV2Service } from '@abovo/shared-services';
import { DossierComponentDossier, DossierItemV2Component, InlineSvgModule } from '@abovo/shared-ui-components';
import { CommonModule } from '@angular/common';
import {
   ChangeDetectionStrategy,
   Component,
   computed,
   effect,
   ElementRef,
   inject,
   model,
   OnInit,
   Renderer2,
   Signal,
   signal,
   untracked,
   viewChild,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { FormControl, FormGroup, FormsModule } from '@angular/forms';
import { MatFabButton } from '@angular/material/button';
import { MatCheckbox } from '@angular/material/checkbox';
import { MatDivider } from '@angular/material/divider';
import { ActivatedRoute } from '@angular/router';
import { FaIconComponent } from '@fortawesome/angular-fontawesome';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { PDFExportModule } from '@progress/kendo-angular-pdf-export';
import { drawDOM, exportPDF } from '@progress/kendo-drawing';
import { saveAs } from '@progress/kendo-file-saver';
import { map } from 'rxjs/operators';
import { REPORT_MODE_ENABLED } from '../../../../constants';
import { StaticReportData } from '../../../media-plans/detail/helpers/static-report.resolver';
import { ManageFlightsComponent } from '../../../media-plans/detail/sub-features/intermediate-report-page/components';
import { IntermediateReportGridContainerComponent } from '../../../media-plans/detail/sub-features/intermediate-report-page/components/intermediate-report-grid-container/intermediate-report-grid-container.component';

@Component({
   selector: 'webvis-feature-static-report',
   standalone: true,
   imports: [
      CommonModule,
      AssetLoaderModule,
      InlineSvgModule,
      MatDivider,
      TranslatePipe,
      IntermediateReportGridContainerComponent,
      DossierItemV2Component,
      MatFabButton,
      FaIconComponent,
      ManageFlightsComponent,
      PDFExportModule,
      MatCheckbox,
      CustomDatePipe,
      FormsModule,
   ],
   templateUrl: './static-report-page.component.html',
   styleUrl: './static-report-page.component.scss',
   changeDetection: ChangeDetectionStrategy.OnPush,
   providers: [
      {
         provide: REPORT_MODE_ENABLED,
         useFactory: () => {
            return signal(true);
         },
      },
   ],
})
export class StaticReportPageComponent implements OnInit {
   private activatedRoute = inject(ActivatedRoute);
   private translate = inject(TranslateService);
   private renderer = inject(Renderer2);
   private themeService = inject(ThemeV2Service);
   private container: Signal<ElementRef<HTMLElement>> = viewChild.required('container', { read: ElementRef });
   private manageFlightComponent = viewChild(ManageFlightsComponent);

   reportData = toSignal<StaticReportData>(this.activatedRoute.data.pipe(map(({ data }) => data)), { requireSync: true });
   report = computed(() => this.reportData().report);
   mediaPlan = computed(() => this.reportData().campaign);
   private fileName = computed(() => `${this.translate.instant('intermediate_report.name')} - ${this.mediaPlan().name}`);
   reportForm = computed(() => new FormGroup({ report_date: new FormControl(this.report().report_date) }));
   targetAudience = computed(() => (this.reportData().campaign.descriptions || []).find((d) => d.type.slug === 'target_audience')?.description);
   dossiersTransformed = computed<DossierComponentDossier[]>(() =>
      this.reportData().dossiers.map((dossier) => {
         let title: string | undefined;
         const isIntermediaReportDossier = dossier.intermediate_report?.id;
         const disciplineName = (dossier.campaign_media_distributions || []).find((cmd) => !!cmd.category)?.category?.name;

         if ((isIntermediaReportDossier && dossier.dossier_type?.slug === 'report_elaboration') || !dossier.dossier_type?.slug) {
            title = disciplineName
               ? this.translate.instant(`dossier.evaluation.label`, { subject: disciplineName })
               : this.translate.instant(`dossier.evaluation.elaboration`);
         }

         return {
            ...dossier,
            title: title,
            employee_id: dossier.creator?.reference,
         };
      }),
   );
   selectedDossierIds = signal<number[]>([]);
   showPlannedColumn = model(true);
   showIntermediateColumn = model(true);
   showRemark = model(!!this.reportData().report.remark);
   showMediaBundles = model(true); // TODO GER-5332

   hiddenFlightIds = computed(() =>
      this.reportData()
         .flights.filter((flight) =>
            this.manageFlightComponent()
               ?.selectedFlightIds()
               .find((flightCmpFlightId) => flightCmpFlightId === flight.id),
         )
         .map((flight) => flight.id),
   );

   private setSelectedDossiers = effect(() => {
      const mappedDossiers = this.dossiersTransformed().map((dossier) => dossier.id);
      untracked(() => this.selectedDossierIds.set(mappedDossiers));
   });

   ngOnInit() {
      // Set the theme to abovomaxlead. Otherwise, the exports can be festively themed, which is funny but not desired.
      this.themeService['setControlsMode']('theme-abovomaxlead.css');
   }

   savePDF() {
      const darkMode = document.documentElement.classList.contains('dark');
      if (darkMode) {
         this.renderer.removeClass(document.documentElement, 'dark');
      }
      drawDOM(this.container().nativeElement)
         .then((group) => {
            if (darkMode) {
               this.renderer.addClass(document.documentElement, 'dark');
            }
            return exportPDF(group, { margin: 16 });
         })
         .then((dataUri) => {
            saveAs(dataUri, this.fileName());
         });
   }

   toggleDossier(dossierId: number) {
      const currentIds = this.selectedDossierIds();
      if (currentIds.includes(dossierId)) {
         this.selectedDossierIds.set(currentIds.filter((id) => id !== dossierId));
      } else {
         this.selectedDossierIds.set([...currentIds, dossierId]);
      }
   }
}
